import { Button, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { iEnvSound, EnvSoundModel } from "../../models/EnvSound";
import api from "../../services/axios-client";
import CircularWithValueLabel from "../../components/CircularWithValueLabel";
import { uploadAsset } from "../../utilities/assetUtils";
import { ASSETS_PATH } from "../../constants";
import Swal from "sweetalert2";

const EnvSound = () => {
    const { env_sound_id } = useParams();
    const navigate = useNavigate();
    const [envSound, setEnvSound] = useState<iEnvSound>(new EnvSoundModel());
    const [uploading, setUploading] = useState<boolean>(false);
    const [progress, setProgress] = useState<number>(0);

    useEffect(() => {
        if (env_sound_id !== "0") {
            fetchEnvSound();
        }
    }, [env_sound_id]);

    const fetchEnvSound = async () => {
        try {
            const response = await api.get(`/env-sounds/${env_sound_id}`);
            if (response.status === 200) {
                setEnvSound(response.data.envSound);
            }
        } catch (error) {
            console.log(error);
        }
    };

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value, type } = e.target;
        let processedValue: string | number = value;
        if (type === 'number') {
            processedValue = value ? parseInt(value, 10) : '';
        }

        setEnvSound((prev) => ({
            ...prev,
            [name]: processedValue,
        }));
    };

    const onSoundFile = (e: any) => {
        uploadAsset(
            e,
            (fileName: string) => {
                setEnvSound({ ...envSound, env_sound: fileName });
            },
            setUploading,
            setProgress
        );
    };

    const onSubmit = async () => {
        if (!envSound.env_sound_name?.trim()) {
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Environment Sound Name is mandatory.'
            });
            return;
        }

        const loopValue = envSound.env_loop;
        if (loopValue === null || loopValue === undefined || loopValue < 1) {
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Sound loop is mandatory and must be at least 1.'
            });
            return;
        }

        try {
            if (env_sound_id !== "0") {
                await api.put(`/env-sounds/${env_sound_id}`, envSound);
                await Swal.fire('Updated!', 'Environment sound updated successfully!', 'success');
            } else {
                await api.post("/env-sounds", envSound);
                await Swal.fire('Created!', 'Environment sound created successfully!', 'success');
            }
            navigate("/env-sounds");
        } catch (error) {
            console.error("Failed to save environment sound:", error);
            Swal.fire('Error!', 'An error occurred while saving the environment sound.', 'error');
        }
    };

    return (
        <>
            <div className="content-header pb-6">
                {env_sound_id === "0" ? "Create Environment Sound" : "Edit Environment Sound"}
            </div>
            <div className="content-body">
                <div className="flex flex-col gap-4 max-w-[600px]">
                    <TextField
                        variant="outlined"
                        label="Sound Name*"
                        name="env_sound_name"
                        value={envSound.env_sound_name || ""}
                        onChange={handleInputChange}
                        fullWidth
                    />

                    {envSound.env_sound && (
                        <div className="flex flex-col gap-2">
                            <audio controls src={`${ASSETS_PATH}/${envSound.env_sound}`}>
                                Your browser does not support the audio element.
                            </audio>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={() => {
                                    setEnvSound({ ...envSound, env_sound: null });
                                }}
                            >
                                Remove Sound File
                            </Button>
                        </div>
                    )}

                    {!envSound.env_sound && (
                        <Button
                            variant="contained"
                            component="label"
                        >
                            Upload Sound File (MP3)
                            <input type="file" hidden onChange={onSoundFile} accept=".mp3,audio/mpeg" />
                        </Button>
                    )}

                    <TextField
                        variant="outlined"
                        label="Beacon ID"
                        name="env_start_beacon_id"
                        value={envSound.env_start_beacon_id || ""}
                        onChange={handleInputChange}
                        fullWidth
                    />                    

                    <TextField
                        variant="outlined"
                        label="Sound loop"
                        name="env_loop"
                        type="number"
                        value={envSound.env_loop}
                        onChange={handleInputChange}
                        fullWidth                        
                        
                    />

                    <hr />

                    <Button variant="contained" color="primary" onClick={onSubmit}>
                        {env_sound_id !== "0" ? "Update Sound" : "Add Sound"}
                    </Button>
                </div>
            </div>
            {uploading && <CircularWithValueLabel progress={progress} />}
        </>
    );
};

export default EnvSound;